/**
 * Evidence Sources Tab - BUG-UI-091 COMPREHENSIVE REDESIGN
 *
 * Shown to AUDITOR disclosure level ONLY.
 *
 * Redesigned (Jan 15, 2026) for Audit Framework compliance with 6 sections:
 * 1. Data Provenance (Origin and Flow)
 * 2. Audit-Grade Lineage Summary
 * 3. Source Data Completeness & Quality
 * 4. Factor Calculation Summary
 * 5. Consent & Access Chain (Regulatory)
 * 6. Evidence Links / Audit Artifacts
 *
 * Supports: FCRA, ECOA, SR 11-7 traceability mandates
 *
 * ⚠️ THIS TAB CONTAINS FULL SOURCE DATA - AUDITOR ONLY
 */

import React, { useState, useEffect } from 'react';
import {
  Database, Building, FileText, ChevronDown, ChevronRight,
  CheckCircle, XCircle, User, Shield, Clock,
  GitBranch, Hash, Link, Eye, FileCheck, Activity, Server, Lock, Send
} from 'lucide-react';
import { EvidenceContract, TransactionItem, ExcludedTransaction, AccountSource, TransactionSources } from '@/types/evidenceContract';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/Tooltip';
import { getSubmissionHistory, SubmissionHistoryResponse } from '@/services/lenderApiService';

/** BUG-UI-061: Income basis data for showing self-reported income reference */
interface IncomeBasisData {
  income_basis_used: 'ATTESTED' | 'DEPOSITED' | 'VERIFIED';
  income_confidence: number;
  income_sources: {
    attested?: number;
    deposited_net?: number;
    verified_gross?: number;
  };
  selection_reason?: string;
}

/** BUG-UI-067/072: Account breakdown from intermediate_metrics */
interface AccountBreakdownItem {
  name: string;
  type: string;
  subtype: string;
  balance: number;
  percentage: number;
  account_id?: string;
  mask?: string;
  institution?: string;
}

/** BUG-UI-097/099: API run metadata type for account/txn counts and provenance */
interface ApiRunMetadata {
  input_account_count?: number | null;
  input_txn_count?: number | null;
  input_months_coverage?: number | null;
  // BUG-UI-099: Plaid snapshot and run timestamp
  plaid_snapshot_id?: string | null;
  run_ts?: string | null;
}

interface EvidenceSourcesTabProps {
  evidence: EvidenceContract | null;
  /** BUG-UI-061: Income basis data for gross_income_6m to show self-reported income */
  incomeBasis?: IncomeBasisData;
  /** BUG-UI-097: Run-level metadata from API (has input_account_count, input_txn_count) */
  apiRunMetadata?: ApiRunMetadata | null;
  /** IMP-LCU-003: Member ID for fetching submission history */
  memberId?: string;
}

export function EvidenceSourcesTab({ evidence, incomeBasis: incomeBasisProp, apiRunMetadata, memberId }: EvidenceSourcesTabProps) {
  const [showIncluded, setShowIncluded] = useState(true);
  const [showExcluded, setShowExcluded] = useState(false);

  // BUG-UI-061: Extract income_basis from evidence.intermediate_metrics if not passed as prop
  // This allows the component to work both with explicit prop and with API data
  const incomeBasis: IncomeBasisData | undefined = incomeBasisProp ||
    (evidence?.intermediate_metrics?.income_basis as IncomeBasisData | undefined);

  // BUG-UI-061: Check if this is an income metric that should show self-reported reference
  const isIncomeMetric = evidence?.metric_key === 'gross_income_6m' ||
                         evidence?.metric_key === 'gross_income_1m' ||
                         evidence?.metric_key === 'yearly_gross_income';
  const hasAttestedIncome = incomeBasis?.income_sources?.attested && incomeBasis.income_sources.attested > 0;

  // BUG-UI-067/072/074: Try to synthesize sources from intermediate_metrics
  const synthesizedSources = synthesizeSourcesFromMetrics(evidence);

  // Detect metric types for appropriate display
  const isVeraScore = evidence?.label?.toLowerCase().includes('verascore') ||
                      evidence?.metric_key === 'verascore_current';
  const isAccountBasedMetric = evidence?.metric_key === 'cash_on_hand' ||
                                evidence?.metric_key === 'total_debt' ||
                                evidence?.metric_key?.includes('liquid_reserves') ||
                                evidence?.metric_key?.includes('balance');
  const isFactorMetric = evidence?.metric_key?.includes('_factor');
  // BUG-UI-067 FIX: DTI ratio is a calculated metric (not transaction-based)
  const isDtiRatioMetric = evidence?.metric_key === 'dti_ratio' ||
                            evidence?.metric_key?.includes('debt_to_income');
  // BUG-UI-074 FIX: Transaction-based metrics that aggregate deposits/withdrawals
  const isTransactionBasedMetric = evidence?.metric_key === 'money_in_1m' ||
                                    evidence?.metric_key === 'money_out_1m' ||
                                    evidence?.metric_key === 'net_savings_1m' ||
                                    evidence?.metric_key === 'money_leftover_1m' ||
                                    evidence?.metric_key?.includes('checking_deposits') ||
                                    evidence?.metric_key?.includes('savings_deposits');

  // BUG-UI-094: ALWAYS show full layout structure for ALL modals
  // Use backend sources if available, otherwise use synthesized
  const sources = evidence?.sources;
  const accounts = sources?.accounts || synthesizedSources.accounts;
  const transactions = sources?.transactions;
  const bureau = sources?.bureau;
  void synthesizedSources.factors; // Reserved for factor provenance UI

  // BUG-UI-094: Generate contextual empty state message based on metric type
  const getEmptyDataMessage = () => {
    if (isVeraScore) {
      return 'VeraScore is calculated from 6 underlying factors. Click on individual factors to see their source data.';
    } else if (isAccountBasedMetric) {
      return 'This metric is derived from account balance data. See the Breakdown tab for account details.';
    } else if (isFactorMetric) {
      return 'This factor score is calculated from underlying metrics. See the Calculation tab for details.';
    } else if (isDtiRatioMetric) {
      return 'DTI Ratio is calculated from debt payments and income. See the Calculation tab for the formula.';
    } else if (isTransactionBasedMetric) {
      // BUG-UI-074 FIX: Improved message for transaction-based metrics
      return 'This metric aggregates transaction data from linked bank accounts. See the Calculation tab for the breakdown by account type.';
    } else if (isIncomeMetric) {
      // BUG-UI-062 FIX: Income metrics derive from transaction deposits
      return 'Income is calculated from deposit transactions across linked accounts. See the Calculation tab for the annualized formula.';
    } else {
      return 'Source data will be populated when transaction contribution data is available.';
    }
  };

  // BUG-UI-091: Extract audit metadata for new sections
  const runMetadata = evidence?.run_metadata;
  const calculation = evidence?.calculation;
  const integrity = evidence?.integrity;
  const metrics = evidence?.intermediate_metrics || {};

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* BUG-UI-091: Tamper-Resistant Architecture Banner */}
        <div className="flex items-center gap-2 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-lg">
          <Shield className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          <div className="flex-1">
            <span className="font-medium text-emerald-800 dark:text-emerald-300 text-sm">
              Tamper-Resistant Audit Trail
            </span>
            <span className="text-emerald-600 dark:text-emerald-400 text-xs ml-2">
              {integrity?.verified ? '✓ Cryptographically Verified' : 'Integrity hash available'}
            </span>
          </div>
          <Badge variant="outline" className="border-emerald-400 text-emerald-700 dark:text-emerald-300 text-xs">
            SR 11-7 Compliant
          </Badge>
        </div>

        {/* SECTION 1: Data Provenance (Origin and Flow) */}
        <SourceSection
          title="Data Provenance"
          icon={<Server className="w-4 h-4" />}
          count={accounts.length}
          defaultExpanded={true}
        >
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Where the underlying data originates and how it flows into this calculation.
            </p>

            {/* Data Sources */}
            <div className="space-y-2">
              {accounts.map((acc, idx) => (
                <DataSourceRow key={idx} account={acc} runMetadata={runMetadata} />
              ))}

              {/* BUG-UI-094: Show contextual message when no account data */}
              {accounts.length === 0 && (
                <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                  <div className="flex items-start gap-2">
                    <Database className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-xs font-medium text-slate-600 dark:text-slate-300">
                        Data Source: Plaid Open Banking
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {getEmptyDataMessage()}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Bureau Data as Source */}
            {bureau && (
              <div className="pt-2 border-t">
                <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-500" />
                    <div>
                      <div className="font-medium text-sm">Credit Bureau</div>
                      <div className="text-xs text-muted-foreground">
                        {bureau.score_model} via CRS
                      </div>
                    </div>
                  </div>
                  <div className="text-right text-xs">
                    <code className="text-muted-foreground">{bureau.bureau_report_id.substring(0, 12)}...</code>
                    <div className="text-muted-foreground">
                      {new Date(bureau.pull_timestamp).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </SourceSection>

        {/* SECTION 2: Audit-Grade Lineage Summary */}
        <SourceSection
          title="Audit Lineage"
          icon={<GitBranch className="w-4 h-4" />}
          count={0}
          defaultExpanded={true}
        >
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Complete traceability chain for audit reproducibility (SR 11-7).
            </p>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <LineageItem
                label="Run ID"
                value={evidence?.run_id || 'N/A'}
                icon={<Hash className="w-3 h-3" />}
                copyable
              />
              <LineageItem
                label="Algorithm Version"
                value={runMetadata?.algorithm_version || calculation?.config_ref?.algorithm_version || 'verascore_v2.3'}
                icon={<Activity className="w-3 h-3" />}
              />
              <LineageItem
                label="Git SHA"
                value={runMetadata?.git_sha || calculation?.config_ref?.git_sha || 'N/A'}
                icon={<GitBranch className="w-3 h-3" />}
                copyable
              />
              <LineageItem
                label="Ruleset Version"
                value={calculation?.config_ref?.ruleset_version || 'tx_rules_v3.2'}
                icon={<FileCheck className="w-3 h-3" />}
              />
              {/* BUG-UI-099: Use apiRunMetadata for plaid_snapshot_id and run_ts */}
              <LineageItem
                label="Plaid Snapshot"
                value={apiRunMetadata?.plaid_snapshot_id || runMetadata?.plaid_snapshot_id || 'N/A'}
                icon={<Database className="w-3 h-3" />}
                copyable
              />
              <LineageItem
                label="Run Timestamp"
                value={apiRunMetadata?.run_ts ? new Date(apiRunMetadata.run_ts).toLocaleString() : runMetadata?.run_ts ? new Date(runMetadata.run_ts).toLocaleString() : evidence?.as_of ? new Date(evidence.as_of).toLocaleString() : 'N/A'}
                icon={<Clock className="w-3 h-3" />}
              />
            </div>

            {/* Integrity Hashes */}
            {integrity && (
              <div className="pt-3 border-t mt-3">
                <div className="text-xs font-medium mb-2 flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Cryptographic Verification
                </div>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Inputs Hash:</span>
                    <code className="text-slate-600 dark:text-slate-400">{integrity.inputs_hash?.substring(0, 20)}...</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Outputs Hash:</span>
                    <code className="text-slate-600 dark:text-slate-400">{integrity.outputs_hash?.substring(0, 20)}...</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Verified:</span>
                    <span className={integrity.verified ? 'text-green-600' : 'text-amber-600'}>
                      {integrity.verified ? '✓ Yes' : '⚠ Pending'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </SourceSection>

        {/* SECTION 3: Data Quality & Completeness */}
        <SourceSection
          title="Data Quality"
          icon={<Activity className="w-4 h-4" />}
          count={0}
          defaultExpanded={true}
        >
          {/* BUG-UI-097: Pass apiRunMetadata for accurate account/txn counts */}
          <DataQualitySection
            runMetadata={runMetadata}
            transactions={transactions}
            accounts={accounts}
            metrics={metrics}
            asOf={evidence?.as_of}
            apiRunMetadata={apiRunMetadata}
          />
        </SourceSection>

        {/* SECTION 4: Consent & Access Chain */}
        {/* FIX: Show expanded for VeraScore/all metrics to match DTI layout */}
        <SourceSection
          title="Consent & Access"
          icon={<Shield className="w-4 h-4" />}
          count={0}
          defaultExpanded={true}
        >
          <ConsentAccessSection evidence={evidence} />
          {/* IMP-LCU-003: Add Submission History to Consent & Access section */}
          {memberId && <SubmissionHistorySection memberId={memberId} />}
        </SourceSection>

        {/* SECTION 5: Evidence Links / Audit Artifacts */}
        {/* FIX: Show expanded for VeraScore/all metrics to match DTI layout */}
        <SourceSection
          title="Audit Artifacts"
          icon={<Link className="w-4 h-4" />}
          count={0}
          defaultExpanded={true}
        >
          <AuditArtifactsSection evidence={evidence} />
        </SourceSection>

        {/* Legacy Transaction Detail (collapsed by default) */}
        {transactions && (transactions.included.length > 0 || transactions.excluded.length > 0) && (
          <SourceSection
            title="Transaction Detail"
            icon={<Database className="w-4 h-4" />}
            count={transactions.included_count + transactions.excluded_count}
            defaultExpanded={false}
          >
            <div className="space-y-4">
              <div className="flex gap-2">
                <Button
                  variant={showIncluded ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowIncluded(!showIncluded)}
                  className="gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  Included ({transactions.included_count})
                </Button>
                <Button
                  variant={showExcluded ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowExcluded(!showExcluded)}
                  className="gap-2"
                >
                  <XCircle className="w-4 h-4" />
                  Excluded ({transactions.excluded_count})
                </Button>
              </div>

              {showIncluded && transactions.included.length > 0 && (
                <TransactionTable transactions={transactions.included} />
              )}

              {showExcluded && transactions.excluded.length > 0 && (
                <ExcludedTable excluded={transactions.excluded} />
              )}
            </div>
          </SourceSection>
        )}

        {/* BUG-UI-061: Self-Reported Income Reference Section */}
        {isIncomeMetric && hasAttestedIncome && (
          <SelfReportedIncomeSection incomeBasis={incomeBasis!} />
        )}
      </div>
    </TooltipProvider>
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/** Collapsible section container */
function SourceSection({ title, icon, count, children, defaultExpanded = true }: {
  title: string;
  icon: React.ReactNode;
  count: number;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-medium text-sm">{title}</span>
          {count > 0 && <Badge variant="secondary" className="text-xs">{count}</Badge>}
        </div>
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      {expanded && <div className="p-3">{children}</div>}
    </div>
  );
}

/** BUG-UI-091: Data Source Row with provenance */
function DataSourceRow({ account, runMetadata }: {
  account: AccountSource;
  runMetadata?: EvidenceContract['run_metadata'];
}) {
  const snapshotId = runMetadata?.plaid_snapshot_id || 'snap-...';
  const retrievedAt = runMetadata?.run_ts || new Date().toISOString();

  return (
    <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
          <Building className="w-4 h-4 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <div className="font-medium text-sm flex items-center gap-2">
            {account.institution}
            <Badge variant="outline" className="text-xs">Plaid</Badge>
          </div>
          <div className="text-xs text-muted-foreground">
            {account.type} •••{account.mask} | {account.subtype || 'checking'}
          </div>
        </div>
      </div>
      <div className="text-right">
        <div className="font-mono text-sm">${account.balance_used.toLocaleString()}</div>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="text-xs text-muted-foreground cursor-help">
              {new Date(retrievedAt).toLocaleDateString()}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">Snapshot: {snapshotId}</p>
            <p className="text-xs">Retrieved: {new Date(retrievedAt).toLocaleString()}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

/** BUG-UI-091: Lineage metadata item */
function LineageItem({ label, value, icon, copyable }: {
  label: string;
  value: string;
  icon: React.ReactNode;
  copyable?: boolean;
}) {
  const handleCopy = () => {
    if (copyable && value !== 'N/A') {
      navigator.clipboard.writeText(value);
    }
  };

  return (
    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
      <div className="flex items-center gap-1 text-muted-foreground mb-1">
        {icon}
        <span>{label}</span>
      </div>
      <div
        className={`font-mono text-xs truncate ${copyable ? 'cursor-pointer hover:text-blue-600' : ''}`}
        onClick={handleCopy}
        title={copyable ? 'Click to copy' : undefined}
      >
        {value}
      </div>
    </div>
  );
}

/** BUG-UI-091: Data Quality Section */
/** EPIC 62 Phase 2B: Enhanced with factor_audit_dto.quality_rollup support */
/** BUG-UI-097: Added apiRunMetadata for accurate account/txn counts from algo_runs table */
function DataQualitySection({ runMetadata, transactions, accounts, metrics, asOf, apiRunMetadata }: {
  runMetadata?: EvidenceContract['run_metadata'];
  transactions?: TransactionSources;
  accounts: AccountSource[];
  metrics: Record<string, unknown>;
  asOf?: string;
  /** BUG-UI-097: Run-level metadata from API response (from algo_runs table) */
  apiRunMetadata?: { input_account_count?: number | null; input_txn_count?: number | null; input_months_coverage?: number | null } | null;
}) {
  // EPIC 62 Phase 2B: Extract quality_rollup from factor_audit_dto
  const factorAuditDTO = metrics.factor_audit_dto as FactorAuditDTOSources | undefined;
  const qualityRollup = factorAuditDTO?.quality_rollup;

  // Calculate data age - prefer quality_rollup.freshness_days when available
  const dataDate = asOf ? new Date(asOf) : new Date();
  const now = new Date();
  const ageInDays = qualityRollup?.freshness_days ?? Math.floor((now.getTime() - dataDate.getTime()) / (1000 * 60 * 60 * 24));
  const isStale = ageInDays > 30;
  const isFresh = ageInDays <= 7;

  // BUG-UI-097/099 FIX: Transaction stats - priority order:
  // 1. quality_rollup.txns_used (factor-specific)
  // 2. transactions.included_count (evidence record)
  // 3. apiRunMetadata.input_txn_count (algo_runs table - most reliable)
  // 4. runMetadata.input_txn_count (evidence contract - may be empty)
  const txnCount = qualityRollup?.txns_used !== undefined ? qualityRollup.txns_used
    : transactions?.included_count !== undefined ? transactions.included_count
    : apiRunMetadata?.input_txn_count ?? runMetadata?.input_txn_count ?? 0;
  const excludedCount = transactions?.excluded_count || 0;
  const totalTxn = txnCount + excludedCount;
  const includeRate = totalTxn > 0 ? Math.round((txnCount / totalTxn) * 100) : 100;

  // BUG-UI-097/099 FIX: Account coverage - priority order:
  // 1. quality_rollup.accounts_used (factor-specific)
  // 2. accounts.length (synthesized from evidence)
  // 3. apiRunMetadata.input_account_count (algo_runs table - most reliable)
  // 4. runMetadata.input_account_count (evidence contract - may be empty)
  const accountCount = qualityRollup?.accounts_used !== undefined ? qualityRollup.accounts_used
    : accounts.length > 0 ? accounts.length
    : apiRunMetadata?.input_account_count ?? runMetadata?.input_account_count ?? 0;
  const monthsCoverage = qualityRollup?.coverage_months !== undefined ? qualityRollup.coverage_months
    : apiRunMetadata?.input_months_coverage ?? runMetadata?.input_months_coverage ?? 0;

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Quality metrics for the underlying data used in this calculation.
      </p>

      <div className="grid grid-cols-2 gap-3">
        {/* Data Freshness */}
        <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">Data Freshness</span>
            <HealthIndicator status={isFresh ? 'good' : isStale ? 'bad' : 'warning'} />
          </div>
          <div className="font-medium text-sm">
            {ageInDays === 0 ? 'Today' : ageInDays === 1 ? '1 day ago' : `${ageInDays} days ago`}
          </div>
          <div className="text-xs text-muted-foreground">
            {dataDate.toLocaleDateString()}
          </div>
        </div>

        {/* Transaction Coverage */}
        <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">Transactions</span>
            <HealthIndicator status={includeRate >= 90 ? 'good' : includeRate >= 70 ? 'warning' : 'bad'} />
          </div>
          <div className="font-medium text-sm">
            {txnCount.toLocaleString()} included
          </div>
          <div className="text-xs text-muted-foreground">
            {excludedCount > 0 ? `${excludedCount} excluded (${includeRate}% rate)` : '100% included'}
          </div>
        </div>

        {/* Account Coverage */}
        <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">Accounts</span>
            <HealthIndicator status={accountCount >= 2 ? 'good' : accountCount === 1 ? 'warning' : 'bad'} />
          </div>
          <div className="font-medium text-sm">
            {accountCount} linked
          </div>
          <div className="text-xs text-muted-foreground">
            {monthsCoverage > 0 ? `${monthsCoverage} months coverage` : 'Current snapshot'}
          </div>
        </div>

        {/* Run Status */}
        {/* BUG-UI-105 FIX: Ensure indicator color matches displayed status */}
        <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">Run Status</span>
            <HealthIndicator status={
              // BUG-UI-105 FIX: Default to 'good' when status is undefined (same as text default)
              !runMetadata?.status || runMetadata.status === 'SUCCESS' ? 'good'
              : runMetadata.status === 'PARTIAL' ? 'warning'
              : 'bad'
            } />
          </div>
          <div className="font-medium text-sm">
            {runMetadata?.status || 'SUCCESS'}
          </div>
          <div className="text-xs text-muted-foreground">
            {runMetadata?.execution_time_ms ? `${runMetadata.execution_time_ms}ms` : 'Complete'}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Health status indicator */
function HealthIndicator({ status }: { status: 'good' | 'warning' | 'bad' }) {
  void status; // colors intentionally replaced by emoji labels below
  const labels = {
    good: '🟢',
    warning: '🟡',
    bad: '🔴',
  };
  return (
    <span className="text-xs" title={status}>
      {labels[status]}
    </span>
  );
}

/** BUG-UI-091: Factor Provenance Row */
export function FactorProvenanceRow({ factor }: {
  factor: { name: string; score: number; weight: number; contribution: number; color: string };
}) {
  const metricKey = factor.name.toLowerCase().replace(/[- ]/g, '_') + '_factor';

  return (
    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: factor.color }} />
          <span className="font-medium text-sm">{factor.name}</span>
          <Badge variant="outline" className="text-xs">
            {(factor.weight * 100).toFixed(0)}% weight
          </Badge>
        </div>
        <div className="text-right">
          <span className="font-mono text-sm">{factor.score}</span>
          <span className="text-xs text-muted-foreground ml-2">→ {factor.contribution.toFixed(1)} pts</span>
        </div>
      </div>
      <div className="text-xs text-muted-foreground">
        <span className="font-mono">metric_key: {metricKey}</span>
      </div>
    </div>
  );
}

/** BUG-UI-091: Consent & Access Chain Section */
function ConsentAccessSection({ evidence }: { evidence: EvidenceContract | null }) {
  // In a real implementation, these would come from the backend
  const consentDate = '2025-11-15T10:30:00Z';
  const sessionId = 'sess_' + (evidence?.run_id || 'unknown').substring(0, 8);

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Chain of custody for data use (FCRA/ECOA compliance).
      </p>

      <div className="space-y-2">
        <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-sm">Consent Given</span>
          </div>
          <div className="text-right text-xs">
            <div className="font-medium">{new Date(consentDate).toLocaleDateString()}</div>
            <div className="text-muted-foreground">Session: {sessionId}</div>
          </div>
        </div>

        <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-blue-500" />
            <span className="text-sm">Permissible Purpose</span>
          </div>
          <div className="text-right text-xs">
            <div className="font-medium">FCRA 604(a)(2)</div>
            <div className="text-muted-foreground">Application for Credit</div>
          </div>
        </div>

        <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-purple-500" />
            <span className="text-sm">Last Accessed</span>
          </div>
          <div className="text-right text-xs">
            <div className="font-medium">Lender Portal</div>
            <div className="text-muted-foreground">{new Date().toLocaleString()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * IMP-LCU-003: Submission History Section
 * Displays recent submissions to lenders for audit compliance
 */
function SubmissionHistorySection({ memberId }: { memberId: string }) {
  const [submissionData, setSubmissionData] = useState<SubmissionHistoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showFullHistory, setShowFullHistory] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchHistory() {
      setIsLoading(true);
      try {
        const data = await getSubmissionHistory(memberId, 3);
        if (!cancelled) {
          setSubmissionData(data);
        }
      } catch (error) {
        if (import.meta.env.DEV) console.error('[SubmissionHistorySection] Error fetching history:', error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchHistory();
    return () => { cancelled = true; };
  }, [memberId]);

  // Format date for display
  const formatDate = (isoDate: string): string => {
    const date = new Date(isoDate);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });
  };

  // Format year_and_month (e.g., "2025-12" -> "Dec 2025")
  const formatYearMonth = (yearMonth: string | null): string => {
    if (!yearMonth) return '—';
    const [year, month] = yearMonth.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleString('en-US', { month: 'short', year: 'numeric' });
  };

  // Format consent scopes for display (e.g., ["verascore", "financial_data"] -> "VeraScore, Financial Data")
  const formatConsentScopes = (scopes: string[] | null): string => {
    if (!scopes || scopes.length === 0) return '—';
    const scopeLabels: Record<string, string> = {
      'verascore': 'VeraScore',
      'financial_data': 'Financial Data',
      'profile': 'Profile',
      'transactions': 'Transactions',
      'credit': 'Credit',
    };
    return scopes.map(s => scopeLabels[s] || s).join(', ');
  };

  if (isLoading) {
    return (
      <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-2 mb-3">
          <Send className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-medium">Submission History</span>
        </div>
        <div className="text-xs text-muted-foreground animate-pulse">Loading submission history...</div>
      </div>
    );
  }

  if (!submissionData || submissionData.totalSubmissions === 0) {
    return (
      <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-2 mb-3">
          <Send className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-medium">Submission History</span>
        </div>
        <p className="text-xs text-muted-foreground">No submissions to lenders yet.</p>
      </div>
    );
  }

  const latestSubmission = submissionData.submissions[0];

  return (
    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
      <div className="flex items-center gap-2 mb-3">
        <Send className="w-4 h-4 text-blue-500" />
        <span className="text-sm font-medium">Submission History</span>
      </div>

      {/* Latest Submission Summary */}
      <div className="space-y-2 mb-3">
        <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded">
          <span className="text-xs text-muted-foreground">Latest Submission</span>
          <span className="text-xs font-medium">{formatDate(latestSubmission.submittedAt)}</span>
        </div>
        <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded">
          <span className="text-xs text-muted-foreground">VeraScore Shared</span>
          <span className="text-xs font-medium">{latestSubmission.verascore ?? '—'}</span>
        </div>
        <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded">
          <span className="text-xs text-muted-foreground">Data Month</span>
          <span className="text-xs font-medium">{formatYearMonth(latestSubmission.yearAndMonth)}</span>
        </div>
        <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded">
          <span className="text-xs text-muted-foreground">Data Shared</span>
          <span className="text-xs font-medium">{formatConsentScopes(latestSubmission.consentScopes)}</span>
        </div>
        <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded">
          <span className="text-xs text-muted-foreground">Total Submissions</span>
          <span className="text-xs font-medium">{submissionData.totalSubmissions}</span>
        </div>
      </div>

      {/* Expandable Full History */}
      {submissionData.submissions.length > 1 && (
        <div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setShowFullHistory(!showFullHistory)}
          >
            {showFullHistory ? (
              <>
                <ChevronDown className="w-3 h-3 mr-1" />
                Hide Full History
              </>
            ) : (
              <>
                <ChevronRight className="w-3 h-3 mr-1" />
                View Full History ({submissionData.submissions.length})
              </>
            )}
          </Button>

          {showFullHistory && (
            <div className="mt-2 space-y-1">
              <div className="grid grid-cols-4 gap-2 text-xs font-medium text-muted-foreground px-2 py-1">
                <span>#</span>
                <span>Date/Time</span>
                <span>Score</span>
                <span>Data Month</span>
              </div>
              {submissionData.submissions.map((sub, idx) => (
                <div
                  key={sub.submissionId}
                  className="grid grid-cols-4 gap-2 text-xs p-2 bg-slate-50 dark:bg-slate-800 rounded"
                >
                  <span className="text-muted-foreground">{idx + 1}</span>
                  <span>{formatDate(sub.submittedAt)}</span>
                  <span className="font-medium">{sub.verascore ?? '—'}</span>
                  <span className="text-muted-foreground">{formatYearMonth(sub.yearAndMonth)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** BUG-UI-091: Audit Artifacts Section */
function AuditArtifactsSection({ evidence }: { evidence: EvidenceContract | null }) {
  const gitSha = evidence?.calculation?.config_ref?.git_sha || evidence?.run_metadata?.git_sha || 'N/A';
  const algorithmVersion = evidence?.run_metadata?.algorithm_version || evidence?.calculation?.config_ref?.algorithm_version || 'verascore_v2.3';

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Links to detailed audit documentation and model artifacts.
      </p>

      <div className="grid grid-cols-2 gap-2">
        <ArtifactLink
          icon={<FileText className="w-4 h-4" />}
          title="Model Card"
          subtitle={algorithmVersion}
          available={true}
        />
        <ArtifactLink
          icon={<Activity className="w-4 h-4" />}
          title="Fairness Report"
          subtitle="Quarterly Review"
          available={false}
        />
        <ArtifactLink
          icon={<GitBranch className="w-4 h-4" />}
          title="Source Code"
          subtitle={`SHA: ${gitSha.substring(0, 7)}`}
          available={gitSha !== 'N/A'}
        />
        <ArtifactLink
          icon={<Database className="w-4 h-4" />}
          title="Evidence JSON"
          subtitle="Raw payload"
          available={true}
          onClick={() => {
            if (evidence) {
              const blob = new Blob([JSON.stringify(evidence, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `evidence_${evidence.metric_key}_${evidence.run_id}.json`;
              a.click();
            }
          }}
        />
      </div>

      <div className="pt-2 border-t text-xs text-muted-foreground">
        <strong>Note:</strong> Some artifacts require elevated access (Auditor role).
      </div>
    </div>
  );
}

/** Artifact link card */
function ArtifactLink({ icon, title, subtitle, available, onClick }: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  available: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={available ? onClick : undefined}
      disabled={!available}
      className={`p-3 rounded border text-left transition-colors ${
        available
          ? 'hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer border-slate-200 dark:border-slate-700'
          : 'opacity-50 cursor-not-allowed border-slate-100 dark:border-slate-800'
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="font-medium text-sm">{title}</span>
      </div>
      <div className="text-xs text-muted-foreground">{subtitle}</div>
      {!available && <div className="text-xs text-amber-600 mt-1">Coming soon</div>}
    </button>
  );
}

function TransactionTable({ transactions }: { transactions: TransactionItem[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b">
            <th className="text-left py-1">Date</th>
            <th className="text-left py-1">Merchant</th>
            <th className="text-left py-1">Category</th>
            <th className="text-right py-1">Amount</th>
          </tr>
        </thead>
        <tbody>
          {transactions.slice(0, 20).map((tx, idx) => (
            <tr key={idx} className="border-b last:border-0">
              <td className="py-1">{new Date(tx.date).toLocaleDateString()}</td>
              <td className="py-1 truncate max-w-[120px]">{tx.merchant}</td>
              <td className="py-1">{tx.category}</td>
              <td className="py-1 text-right font-mono">
                ${Math.abs(tx.amount).toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {transactions.length > 20 && (
        <div className="text-center text-xs text-muted-foreground py-2">
          +{transactions.length - 20} more transactions
        </div>
      )}
    </div>
  );
}

function ExcludedTable({ excluded }: { excluded: ExcludedTransaction[] }) {
  return (
    <div className="space-y-1">
      {excluded.slice(0, 10).map((tx, idx) => (
        <div key={idx} className="flex items-center justify-between text-xs py-1 border-b last:border-0">
          <code className="text-muted-foreground">{tx.tx_id.substring(0, 12)}...</code>
          <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
            {tx.reason.replace('_', ' ')}
          </Badge>
        </div>
      ))}
      {excluded.length > 10 && (
        <div className="text-center text-xs text-muted-foreground py-2">
          +{excluded.length - 10} more excluded
        </div>
      )}
    </div>
  );
}

/**
 * BUG-UI-061: Self-Reported Income Section
 * Shows the user's attested/self-reported income as a reference point for comparison
 * with the deposited/verified income shown in the Summary tab.
 */
function SelfReportedIncomeSection({ incomeBasis }: { incomeBasis: IncomeBasisData }) {
  const attestedIncome = incomeBasis.income_sources.attested || 0;
  const depositedIncome = incomeBasis.income_sources.deposited_net || 0;

  // Calculate variance if both values available
  const hasVariance = attestedIncome > 0 && depositedIncome > 0;
  const varianceAmount = hasVariance ? attestedIncome - depositedIncome : 0;
  const variancePercent = hasVariance ? Math.abs((varianceAmount / attestedIncome) * 100) : 0;
  const isOverstated = varianceAmount > 0; // User reported higher than deposited

  return (
    <div className="border rounded-lg overflow-hidden bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700">
      <div className="p-3 flex items-center gap-2 bg-amber-100 dark:bg-amber-900/30">
        <User className="w-4 h-4 text-amber-700 dark:text-amber-400" />
        <span className="font-medium text-sm text-amber-800 dark:text-amber-300">
          Self-Reported Income (User Attested)
        </span>
        <Badge variant="outline" className="text-xs border-amber-400 text-amber-700 dark:text-amber-300">
          Onboarding Input
        </Badge>
      </div>
      <div className="p-4 space-y-3">
        {/* Self-Reported Value */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-amber-700 dark:text-amber-400">Annual Income (User-Reported)</div>
            <div className="text-lg font-semibold text-amber-900 dark:text-amber-200">
              ${attestedIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500 dark:text-slate-400">Source</div>
            <div className="text-sm text-slate-700 dark:text-slate-300">Member Onboarding</div>
          </div>
        </div>

        {/* Variance Explanation */}
        {hasVariance && variancePercent > 5 && (
          <div className={`p-3 rounded-lg ${isOverstated ? 'bg-amber-200/50 dark:bg-amber-800/30' : 'bg-green-100 dark:bg-green-900/30'}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Variance from Deposited Income
              </span>
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-400">
              {isOverstated ? (
                <>
                  Self-reported is <strong>${varianceAmount.toLocaleString()}</strong> higher ({variancePercent.toFixed(1)}%) than verified deposits.
                  This may indicate non-deposited income (cash, unreported sources) or user overestimation.
                </>
              ) : (
                <>
                  Self-reported is <strong>${Math.abs(varianceAmount).toLocaleString()}</strong> lower ({variancePercent.toFixed(1)}%) than verified deposits.
                  User may have underestimated their income.
                </>
              )}
            </div>
          </div>
        )}

        {/* Data Quality Note */}
        <div className="text-xs text-slate-500 dark:text-slate-400 pt-2 border-t border-amber-200 dark:border-amber-700">
          <strong>Note:</strong> Self-reported income is captured during member onboarding.
          The dashboard displays <em>Deposited Income</em> (verifiable via bank data) for lender decision-making.
        </div>
      </div>
    </div>
  );
}

/**
 * BUG-UI-067/072/074/075: Synthesize sources from intermediate_metrics
 * When backend doesn't populate sources section, we can extract account info
 * from the intermediate_metrics.account_breakdown array.
 */
interface SynthesizedSources {
  accounts: AccountSource[];
  factors: Array<{
    name: string;
    score: number;
    weight: number;
    contribution: number;
    color: string;
  }>;
}

/** EPIC 62 Phase 2B: FactorAuditDTO structure for source extraction */
interface FactorAuditDTOSources {
  inputs?: Array<{
    name: string;
    value: number;
    unit: string;
    basis?: string;
    source_refs?: Array<{
      type: 'PLAID' | 'ATTESTED' | 'CRS' | 'VERIFIED';
      account_id?: string;
      institution?: string;
      mask?: string;
    }>;
  }>;
  quality_rollup?: {
    accounts_used: number;
    txns_used: number;
    coverage_months: number;
    freshness_days: number;
  };
}

function synthesizeSourcesFromMetrics(evidence: EvidenceContract | null): SynthesizedSources {
  const result: SynthesizedSources = { accounts: [], factors: [] };

  if (!evidence?.intermediate_metrics) {
    return result;
  }

  const metrics = evidence.intermediate_metrics;
  const metricKey = evidence.metric_key || '';

  // EPIC 62 Phase 2B: Extract factor_audit_dto for enhanced source information
  const factorAuditDTO = metrics.factor_audit_dto as FactorAuditDTOSources | undefined;

  // EPIC 62 Phase 2B: If factor_audit_dto has inputs with source_refs, use those first
  if (factorAuditDTO?.inputs) {
    const seenAccountIds = new Set<string>();

    for (const input of factorAuditDTO.inputs) {
      if (input.source_refs && input.source_refs.length > 0) {
        for (const ref of input.source_refs) {
          const accountId = ref.account_id || `${ref.type}-${input.name}`;

          // Avoid duplicates
          if (seenAccountIds.has(accountId)) continue;
          seenAccountIds.add(accountId);

          result.accounts.push({
            account_id: accountId,
            mask: ref.mask || '****',
            institution: ref.institution || `${ref.type} Source`,
            type: 'depository' as AccountSource['type'],
            subtype: ref.type.toLowerCase(),
            balance_current: input.value,
            balance_used: input.value,
            include_reason: `Source for ${input.name} (${input.basis || ref.type})`,
          });
        }
      }
    }

    // If we found sources from factor_audit_dto, return early
    if (result.accounts.length > 0) {
      return result;
    }
  }

  // BUG-UI-067/072: Extract accounts from account_breakdown
  const accountBreakdown = metrics.account_breakdown as AccountBreakdownItem[] | undefined;
  if (accountBreakdown && Array.isArray(accountBreakdown)) {
    result.accounts = accountBreakdown.map((acc, idx) => ({
      account_id: acc.account_id || `synth-${idx}`,
      mask: acc.mask || '****',
      institution: acc.institution || acc.name || 'Plaid Connected',
      type: (acc.type === 'depository' ? 'depository' :
             acc.type === 'credit' ? 'credit' :
             acc.type === 'loan' ? 'loan' : 'depository') as AccountSource['type'],
      subtype: acc.subtype,
      balance_current: acc.balance,
      balance_used: acc.balance,
      include_reason: `${acc.subtype || acc.type} account (${acc.percentage?.toFixed(0) || 0}% of total)`,
    }));
  }

  // BUG-UI-070-075: For factor entities, synthesize accounts from relevant metrics
  // Each factor uses different underlying data sources
  const isFactorEntity = metricKey.includes('_factor') ||
    ['cash_flow', 'savings', 'liquid_reserves', 'debt_to_income', 'debt_management', 'payment_history']
      .some(f => metricKey.includes(f));

  if (isFactorEntity && result.accounts.length === 0) {
    // Try to extract account info from factor-specific metrics
    const accountsUsed = metrics.accounts_used as Array<{ name: string; type: string; balance: number }> | undefined;
    if (accountsUsed && Array.isArray(accountsUsed)) {
      result.accounts = accountsUsed.map((acc, idx) => ({
        account_id: `factor-${idx}`,
        mask: '****',
        institution: acc.name || 'Linked Account',
        type: (acc.type === 'depository' ? 'depository' :
               acc.type === 'credit' ? 'credit' : 'depository') as AccountSource['type'],
        subtype: acc.type,
        balance_current: acc.balance,
        balance_used: acc.balance,
        include_reason: 'Used in factor calculation',
      }));
    }

    // BUG-UI-072 FIX: For liquid_reserves_factor, synthesize accounts from cash_on_hand components
    if (metricKey.includes('liquid_reserves') && result.accounts.length === 0) {
      // Try to get cash breakdown from intermediate metrics
      const totalCash = metrics.total_cash_on_hand as number | undefined;
      const savingsBalance = metrics.savings_balance as number | undefined;
      const checkingBalance = metrics.checking_balance as number | undefined;

      if (typeof totalCash === 'number' && totalCash > 0) {
        // Synthesize account entries from available balance data
        if (typeof checkingBalance === 'number' && checkingBalance > 0) {
          result.accounts.push({
            account_id: 'synth-checking',
            mask: '****',
            institution: 'Linked Checking Account',
            type: 'depository',
            subtype: 'checking',
            balance_current: checkingBalance,
            balance_used: checkingBalance,
            include_reason: `${((checkingBalance / totalCash) * 100).toFixed(0)}% of liquid reserves`,
          });
        }
        if (typeof savingsBalance === 'number' && savingsBalance > 0) {
          result.accounts.push({
            account_id: 'synth-savings',
            mask: '****',
            institution: 'Linked Savings Account',
            type: 'depository',
            subtype: 'savings',
            balance_current: savingsBalance,
            balance_used: savingsBalance,
            include_reason: `${((savingsBalance / totalCash) * 100).toFixed(0)}% of liquid reserves`,
          });
        }
      }
    }
  }

  // BUG-UI-074 FIX: For transaction-based metrics, show summary info
  const isTransactionMetric = metricKey === 'money_in_1m' ||
    metricKey === 'money_out_1m' ||
    metricKey === 'net_savings_1m' ||
    metricKey === 'money_leftover_1m' ||
    metricKey.includes('deposits') ||
    metricKey.includes('withdrawals');

  if (isTransactionMetric && result.accounts.length === 0) {
    // Try to extract deposit/withdrawal breakdown
    const checkingDeposits = metrics.checking_deposits as number | undefined;
    const savingsDeposits = metrics.savings_deposits as number | undefined;

    if (typeof checkingDeposits === 'number' || typeof savingsDeposits === 'number') {
      if (typeof checkingDeposits === 'number') {
        result.accounts.push({
          account_id: 'txn-checking',
          mask: '****',
          institution: 'Checking Deposits',
          type: 'depository',
          subtype: 'checking',
          balance_current: checkingDeposits,
          balance_used: checkingDeposits,
          include_reason: `Deposits from checking accounts`,
        });
      }
      if (typeof savingsDeposits === 'number') {
        result.accounts.push({
          account_id: 'txn-savings',
          mask: '****',
          institution: 'Savings Deposits',
          type: 'depository',
          subtype: 'savings',
          balance_current: savingsDeposits,
          balance_used: savingsDeposits,
          include_reason: `Deposits from savings accounts`,
        });
      }
    }
  }

  // BUG-UI-068: Extract factors for VeraScore
  if (metricKey === 'verascore_current') {
    const factorColors: Record<string, string> = {
      cash_flow: '#22c55e',
      savings: '#3b82f6',
      liquid_reserves: '#8b5cf6',
      debt_to_income: '#f59e0b',
      debt_management: '#ef4444',
      payment_history: '#ec4899',
    };

    const factorWeights: Record<string, number> = {
      cash_flow: 0.15,
      savings: 0.12,
      liquid_reserves: 0.12,
      debt_to_income: 0.20,
      debt_management: 0.175,
      payment_history: 0.10,
    };

    const factorNames: Record<string, string> = {
      cash_flow: 'Cash Flow',
      savings: 'Savings',
      liquid_reserves: 'Liquid Reserves',
      debt_to_income: 'Debt-to-Income',
      debt_management: 'Debt Management',
      payment_history: 'Payment History',
    };

    // BUG-UI-054/068: Try multiple key patterns for factor scores
    // Backend uses different naming conventions depending on calculation stage
    for (const factorKey of Object.keys(factorNames)) {
      // Try all possible key variations
      const possibleKeys = [
        `${factorKey}_score`,           // e.g., cash_flow_score
        `stage1_${factorKey}_score`,    // e.g., stage1_debt_to_income_score
        `stage2_${factorKey}_score`,    // e.g., stage2_cash_flow_score
        `${factorKey}_factor`,          // e.g., cash_flow_factor
        factorKey,                       // e.g., cash_flow (raw score)
      ];

      let score: number | undefined;
      for (const key of possibleKeys) {
        const val = metrics[key];
        // BUG-UI-068 FIX: Include 0 scores - they're valid (e.g., savings=0 when no savings)
        // Only skip undefined/null values
        if (typeof val === 'number') {
          score = val;
          break;
        }
      }

      // BUG-UI-068 FIX: Check if score was found (0 is valid score)
      if (score !== undefined) {
        const weight = factorWeights[factorKey] || 0.15;
        result.factors.push({
          name: factorNames[factorKey],
          score: Math.round(score),
          weight,
          contribution: score * weight,
          color: factorColors[factorKey] || '#6b7280',
        });
      }
    }

    // Sort by weight (highest first)
    result.factors.sort((a, b) => b.weight - a.weight);
  }

  return result;
}
